import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";

export interface FeedbackThread {
  id: string;
  user_id: string;
  user_name_snapshot: string | null;
  user_email?: string | null;
  title: string;
  status: "in_review" | "awaiting_user" | "awaiting_finalization" | "finalized";
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  last_admin_reply_at: string | null;
  last_user_reply_at: string | null;
  user_unread_count: number;
  admin_unread_count: number;
  closed_at: string | null;
  finalized_by_admin_at: string | null;
  resolved_by_user_at: string | null;
}

export interface FeedbackMessage {
  id: string;
  thread_id: string;
  sender_type: "user" | "admin";
  sender_name: string | null;
  message: string;
  created_at: string;
  is_system_message: boolean;
}

export interface FeedbackAttachment {
  id: string;
  thread_id: string;
  message_id: string | null;
  image_url: string;
  sort_order: number;
  created_at: string;
}

export const STATUS_CONFIG = {
  in_review: { label: "feedback_thread.status_in_review", color: "bg-orange-100 text-orange-700 border-orange-200" },
  awaiting_user: { label: "feedback_thread.status_awaiting_user", color: "bg-purple-100 text-purple-700 border-purple-200" },
  awaiting_finalization: { label: "feedback_thread.status_awaiting_finalization", color: "bg-blue-100 text-blue-700 border-blue-200" },
  finalized: { label: "feedback_thread.status_finalized", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
} as const;

export function useUserThreads() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["feedback-threads", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("feedback_threads")
        .select("*")
        .eq("user_id", user.id)
        .order("last_message_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as FeedbackThread[];
    },
    enabled: !!user?.id,
  });

  // Realtime
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`feedback_threads_rt:${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "feedback_threads", filter: `user_id=eq.${user.id}` }, () => {
        queryClient.invalidateQueries({ queryKey: ["feedback-threads", user.id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, queryClient]);

  const hasUnread = (query.data ?? []).some(t => t.user_unread_count > 0);

  return { ...query, hasUnread };
}

export function useThreadMessages(threadId: string | undefined) {
  const queryClient = useQueryClient();

  const messagesQuery = useQuery({
    queryKey: ["feedback-messages", threadId],
    queryFn: async () => {
      if (!threadId) return [];
      const { data, error } = await supabase
        .from("feedback_messages")
        .select("*")
        .eq("thread_id", threadId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as FeedbackMessage[];
    },
    enabled: !!threadId,
    staleTime: 1000 * 60 * 2,   // 2 min — realtime channel handles instant updates
    gcTime:    1000 * 60 * 10,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const attachmentsQuery = useQuery({
    queryKey: ["feedback-attachments", threadId],
    queryFn: async () => {
      if (!threadId) return [];
      const { data, error } = await supabase
        .from("feedback_attachments")
        .select("*")
        .eq("thread_id", threadId)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      const attachments = (data ?? []) as FeedbackAttachment[];

      // Generate fresh signed URLs for storage paths
      const withUrls = await Promise.all(
        attachments.map(async (att) => {
          // If image_url is already an absolute URL with /object/sign/ it's a legacy signed URL
          // or if it's a relative path, generate a fresh signed URL
          const isStoragePath = !att.image_url.startsWith("http");
          if (isStoragePath) {
            const { data: signedData } = await supabase.storage
              .from("feedback-attachments")
              .createSignedUrl(att.image_url, 3600); // 1 hour
            return { ...att, image_url: signedData?.signedUrl || att.image_url };
          }
          // Legacy signed URL — try to re-sign by extracting path
          const pathMatch = att.image_url.match(/\/object\/sign\/feedback-attachments\/(.+?)(\?|$)/);
          if (pathMatch) {
            const storagePath = decodeURIComponent(pathMatch[1]);
            const { data: signedData } = await supabase.storage
              .from("feedback-attachments")
              .createSignedUrl(storagePath, 3600);
            return { ...att, image_url: signedData?.signedUrl || att.image_url };
          }
          return att;
        })
      );
      return withUrls;
    },
    enabled: !!threadId,
    staleTime: 1000 * 60 * 50,  // 50 min — signed URLs valid for 1 hour
    gcTime:    1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  // Realtime for messages
  useEffect(() => {
    if (!threadId) return;
    const channel = supabase
      .channel(`feedback_msgs_rt:${threadId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "feedback_messages", filter: `thread_id=eq.${threadId}` }, () => {
        queryClient.invalidateQueries({ queryKey: ["feedback-messages", threadId] });
        queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [threadId, queryClient]);

  return { messages: messagesQuery, attachments: attachmentsQuery };
}

export function useCreateThread() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ title, message, images, category_key }: { title: string; message: string; images: File[]; category_key?: string }) => {
      if (!user?.id) throw new Error("Not authenticated");

      // 1. Create thread
      const { data: thread, error: threadError } = await supabase
        .from("feedback_threads")
        .insert({
          user_id: user.id,
          user_name_snapshot: user.display_name || null,
          title: title.trim(),
          status: "in_review" as any,
          admin_unread_count: 1,
        })
        .select("id")
        .single();
      if (threadError) throw threadError;

      // 2. Create first message
      const { data: msg, error: msgError } = await supabase
        .from("feedback_messages")
        .insert({
          thread_id: thread.id,
          sender_type: "user",
          sender_name: user.display_name || null,
          message: message.trim(),
        })
        .select("id")
        .single();
      if (msgError) throw msgError;

      // 3. Upload images
      if (images.length > 0) {
        const attachments: { thread_id: string; message_id: string; image_url: string; sort_order: number }[] = [];
        for (let i = 0; i < images.length; i++) {
          try {
            const file = images[i];
            const ext = file.name.split(".").pop() || "jpg";
            const path = `${user.id}/${thread.id}/${Date.now()}_${i}.${ext}`;
            const { error: uploadError } = await supabase.storage
              .from("feedback-attachments")
              .upload(path, file, { contentType: file.type });
            if (uploadError) {
              console.error(`[Feedback] Image upload ${i} failed:`, uploadError.message);
              continue;
            }
            attachments.push({ thread_id: thread.id, message_id: msg.id, image_url: path, sort_order: i });
          } catch (err) {
            console.error(`[Feedback] Image upload ${i} exception:`, err);
          }
        }
        if (attachments.length > 0) {
          const { error: attachError } = await supabase
            .from("feedback_attachments")
            .insert(attachments);
          if (attachError) console.error("[Feedback] Attach insert error:", attachError);
        }
      }

      // 4. Trigger bot (fire-and-forget — doesn't block the user)
      supabase.functions.invoke("ai-support-bot", {
        body: { thread_id: thread.id, category_key, is_followup: false },
      }).catch((e) => console.warn("[support-bot] invoke error:", e));

      return thread.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
    },
  });
}

export function useSendReply() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadId, message, images }: { threadId: string; message: string; images?: File[] }) => {
      if (!user?.id) throw new Error("Not authenticated");

      // Create message
      const { data: msg, error: msgError } = await supabase
        .from("feedback_messages")
        .insert({
          thread_id: threadId,
          sender_type: "user",
          sender_name: user.display_name || null,
          message: message.trim(),
        })
        .select("id")
        .single();
      if (msgError) throw msgError;

      // Upload images if any
      if (images && images.length > 0) {
        const attachments: { thread_id: string; message_id: string; image_url: string; sort_order: number }[] = [];
        for (let i = 0; i < images.length; i++) {
          const file = images[i];
          const ext = file.name.split(".").pop() || "jpg";
          const path = `${user.id}/${threadId}/${Date.now()}_${i}.${ext}`;
          const { error: uploadError } = await supabase.storage
            .from("feedback-attachments")
            .upload(path, file, { contentType: file.type });
          if (uploadError) {
            console.error(`[Feedback] Image upload ${i} failed:`, uploadError.message);
            throw new Error(`Falha ao enviar imagem: ${uploadError.message}`);
          }
          attachments.push({ thread_id: threadId, message_id: msg.id, image_url: path, sort_order: i });
        }
        if (attachments.length > 0) {
          const { error: attachError } = await supabase.from("feedback_attachments").insert(attachments);
          if (attachError) throw new Error(`Falha ao salvar imagem: ${attachError.message}`);
        }
      }

      // Update thread status back to in_review + timestamps
      await supabase
        .from("feedback_threads")
        .update({
          status: "in_review" as any,
          last_message_at: new Date().toISOString(),
          last_user_reply_at: new Date().toISOString(),
          admin_unread_count: 1,
        } as any)
        .eq("id", threadId);

      // Trigger bot for follow-up (fire-and-forget)
      supabase.functions.invoke("ai-support-bot", {
        body: { thread_id: threadId, is_followup: true },
      }).catch((e) => console.warn("[support-bot] invoke error:", e));

    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["feedback-messages", vars.threadId] });
      queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
    },
  });
}

// ── Auto-install reply ────────────────────────────────────────────
const INSTALL_KEYWORDS = [
  "install", "app store", "google play", "download", "home screen",
  "instalar", "descargar", "pantalla de inicio",
  "installieren", "herunterladen", "startbildschirm",
];

export function isInstallMessage(text: string): boolean {
  const lower = text.toLowerCase();
  return INSTALL_KEYWORDS.some((kw) => lower.includes(kw));
}

const AUTO_INSTALL_MESSAGES: Record<string, string> = {
  en: `Hi 💛\n\nThe app is not downloaded from the App Store or Google Play. It works through your browser and can be added to your phone's home screen.\n\nPlease open your access link directly in Safari and follow these steps:\n\niPhone / iPad (iOS 26):\n1. Open the app link in Safari\n2. Tap the three dots (•••) next to the address bar\n3. Tap Share\n4. Tap More\n5. Tap Add to Home Screen\n6. Enable the option "Open as Web App"\n\niPhone / iPad (older iOS):\n1. Open the app link in Safari\n2. Tap the Share button — the square with an arrow pointing up\n3. Scroll down and tap Add to Home Screen\n4. Tap Add\n\nAndroid:\n1. Open the app link in Google Chrome\n2. Tap the three-dot menu in the top right\n3. Tap Install app or Add to Home screen\n4. Confirm by tapping Add or Install\n\nAfter that, the app should appear on your home screen as Glucofy.\n\nIf it still doesn't work, please send us a screenshot of what you see and we'll guide you step by step.\n\nSincerely,\nChef Natasha's Support Team 💛`,

  es: `Hola 💛\n\nLa app no se descarga desde App Store ni Google Play. Funciona desde el navegador y puedes agregarla a la pantalla de inicio de tu celular.\n\nPor favor, abre tu enlace de acceso directamente en Safari y sigue estos pasos:\n\niPhone / iPad (iOS 26):\n1. Abre el enlace de la app en Safari\n2. Toca los tres puntos (•••) al lado de la barra de búsqueda\n3. Toca Compartir\n4. Toca Más\n5. Toca Agregar a pantalla de inicio\n6. Activa la opción "Abrir como app web"\n\niPhone / iPad (iOS anterior):\n1. Abre el enlace de la app en Safari\n2. Toca el botón de Compartir — el cuadrado con una flecha hacia arriba\n3. Desplázate hacia abajo y toca Agregar a pantalla de inicio\n4. Toca Agregar\n\nAndroid:\n1. Abre el enlace de la app en Google Chrome\n2. Toca el menú de tres puntos en la parte superior derecha\n3. Toca Instalar app o Agregar a pantalla de inicio\n4. Confirma tocando Agregar o Instalar\n\nDespués de eso, la app debería aparecer en tu pantalla de inicio como Glucofy.\n\nSi todavía no funciona, por favor envíanos una captura de pantalla de lo que ves y te guiaremos paso a paso.\n\nAtentamente,\nEl equipo de soporte de la chef Natasha 💛`,

  de: `Hallo 💛\n\nDie App wird nicht aus dem App Store oder Google Play heruntergeladen. Sie funktioniert über den Browser und kann zum Startbildschirm Ihres Handys hinzugefügt werden.\n\nBitte öffnen Sie Ihren Zugangslink direkt in Safari und folgen Sie diesen Schritten:\n\niPhone / iPad (iOS 26):\n1. Öffnen Sie den App-Link in Safari\n2. Tippen Sie auf die drei Punkte (•••) neben der Adressleiste\n3. Tippen Sie auf Teilen\n4. Tippen Sie auf Mehr\n5. Tippen Sie auf Zum Home-Bildschirm hinzufügen\n6. Aktivieren Sie die Option "Als Web-App öffnen"\n\niPhone / iPad (älteres iOS):\n1. Öffnen Sie den App-Link in Safari\n2. Tippen Sie auf das Teilen-Symbol — das Quadrat mit dem Pfeil nach oben\n3. Scrollen Sie nach unten und tippen Sie auf Zum Home-Bildschirm hinzufügen\n4. Tippen Sie auf Hinzufügen\n\nAndroid:\n1. Öffnen Sie den App-Link in Google Chrome\n2. Tippen Sie oben rechts auf das Drei-Punkte-Menü\n3. Tippen Sie auf App installieren oder Zum Startbildschirm hinzufügen\n4. Bestätigen Sie mit Hinzufügen oder Installieren\n\nDanach sollte die App auf Ihrem Startbildschirm als Glucofy erscheinen.\n\nWenn es weiterhin nicht funktioniert, senden Sie uns bitte einen Screenshot und wir führen Sie Schritt für Schritt.\n\nMit freundlichen Grüßen,\nChef Natashas Support-Team 💛`,
};

export function getAutoInstallMessage(language: string): string {
  const lang = language.substring(0, 2).toLowerCase();
  return AUTO_INSTALL_MESSAGES[lang] ?? AUTO_INSTALL_MESSAGES["es"];
}

export function useAutoInstallReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadId, language }: { threadId: string; language: string }) => {
      const message = getAutoInstallMessage(language);

      const { error: msgError } = await supabase.from("feedback_messages").insert({
        thread_id: threadId,
        sender_type: "admin",
        sender_name: "AutoBot",
        message,
        is_system_message: false,
      });
      if (msgError) throw msgError;

      // Keep thread status as awaiting_user — do NOT flip to in_review,
      // do NOT increment admin_unread_count.
      await supabase
        .from("feedback_threads")
        .update({
          status: "awaiting_user" as any,
          last_message_at: new Date().toISOString(),
          user_unread_count: 1,
        } as any)
        .eq("id", threadId);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["feedback-messages", vars.threadId] });
      queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
    },
  });
}

// ── Auto flour-substitute reply ──────────────────────────────────
const FLOUR_SUBSTITUTE_KEYWORDS = [
  "almond flour", "nut allergy", "tree nut", "sunflower", "substitute flour", "flour substitute",
  "farinha de amêndoa", "alergia",
];

export function isFlourSubstituteMessage(text: string): boolean {
  const lower = text.toLowerCase();
  return FLOUR_SUBSTITUTE_KEYWORDS.some((kw) => lower.includes(kw));
}

const AUTO_FLOUR_SUBSTITUTE_MESSAGES: Record<string, string> = {
  en: `Hi 💛\n\nGreat news — almond flour is easy to swap if you're avoiding nuts. Sunflower seed flour works as a straight 1:1 replacement in almost every recipe. If you'd rather use coconut flour, use only about ¼ of the amount the recipe calls for, since it absorbs a lot more liquid — you'll also need to add a bit more liquid (milk, water, or eggs) to keep the texture right.\n\nOne more tip: right inside any recipe, tap the ↺ icon next to any ingredient and the app will suggest substitutes for you automatically, tailored to that specific recipe.\n\nSincerely,\nChef Natasha's Support Team 💛`,

  es: `Hola 💛\n\n¡Buenas noticias! La harina de almendra es fácil de reemplazar si evitas los frutos secos. La harina de semilla de girasol funciona como reemplazo directo 1:1 en casi todas las recetas. Si prefieres usar harina de coco, usa solo ¼ de la cantidad que pide la receta, ya que absorbe mucho más líquido — también necesitarás agregar un poco más de líquido (leche, agua o huevos) para mantener la textura correcta.\n\nUn tip más: dentro de cualquier receta, toca el ícono ↺ junto a cualquier ingrediente y la app te sugerirá sustitutos automáticamente, adaptados a esa receta específica.\n\nAtentamente,\nEl equipo de soporte de la chef Natasha 💛`,
};

export function getAutoFlourSubstituteMessage(language: string): string {
  const lang = language.substring(0, 2).toLowerCase();
  return AUTO_FLOUR_SUBSTITUTE_MESSAGES[lang] ?? AUTO_FLOUR_SUBSTITUTE_MESSAGES["es"];
}

export function useAutoFlourSubstituteReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadId, language }: { threadId: string; language: string }) => {
      const message = getAutoFlourSubstituteMessage(language);

      const { error: msgError } = await supabase.from("feedback_messages").insert({
        thread_id: threadId,
        sender_type: "admin",
        sender_name: "AutoBot",
        message,
        is_system_message: false,
      });
      if (msgError) throw msgError;

      // Keep thread status as awaiting_user — do NOT flip to in_review,
      // do NOT increment admin_unread_count.
      await supabase
        .from("feedback_threads")
        .update({
          status: "awaiting_user" as any,
          last_message_at: new Date().toISOString(),
          user_unread_count: 1,
        } as any)
        .eq("id", threadId);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["feedback-messages", vars.threadId] });
      queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
    },
  });
}

// ── Auto subscription-vs-one-time reply ──────────────────────────
const SUBSCRIPTION_KEYWORDS = [
  "subscription", "monthly", "one time", "charged monthly",
  "suscripción", "mensual", "pago único", "assinatura",
];

export function isSubscriptionMessage(text: string): boolean {
  const lower = text.toLowerCase();
  return SUBSCRIPTION_KEYWORDS.some((kw) => lower.includes(kw));
}

const AUTO_SUBSCRIPTION_MESSAGES: Record<string, string> = {
  en: `Hi 💛\n\nJust to clear this up: all of our recipe modules — Clean Sweets, Clean Bakes, Clean Meals, Panadería Cero, and the rest — are one-time purchases, not subscriptions. Once you buy one, it's yours forever, with no recurring charge. The only product with a monthly charge is Glucofy AI.\n\nIf you're seeing a monthly charge and you did not purchase Glucofy AI, that charge isn't coming from us — please reach out directly to the payment platform you bought through (Hotmart or Digistore24) with your order number, and their support team can look into it and cancel it if needed.\n\nSincerely,\nChef Natasha's Support Team 💛`,

  es: `Hola 💛\n\nPara aclararlo: todos nuestros módulos de recetas — Clean Sweets, Clean Bakes, Clean Meals, Panadería Cero, y los demás — son de pago único, no una suscripción. Una vez que los compras, son tuyos para siempre, sin cobros recurrentes. El único producto con cobro mensual es Glucofy AI.\n\nSi estás viendo un cobro mensual y no compraste Glucofy AI, ese cobro no viene de nosotros — por favor contacta directamente a la plataforma de pago por la que compraste (Hotmart o Digistore24) con tu número de pedido, y su equipo de soporte podrá revisarlo y cancelarlo si es necesario.\n\nAtentamente,\nEl equipo de soporte de la chef Natasha 💛`,
};

export function getAutoSubscriptionMessage(language: string): string {
  const lang = language.substring(0, 2).toLowerCase();
  return AUTO_SUBSCRIPTION_MESSAGES[lang] ?? AUTO_SUBSCRIPTION_MESSAGES["es"];
}

export function useAutoSubscriptionReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadId, language }: { threadId: string; language: string }) => {
      const message = getAutoSubscriptionMessage(language);

      const { error: msgError } = await supabase.from("feedback_messages").insert({
        thread_id: threadId,
        sender_type: "admin",
        sender_name: "AutoBot",
        message,
        is_system_message: false,
      });
      if (msgError) throw msgError;

      // Keep thread status as awaiting_user — do NOT flip to in_review,
      // do NOT increment admin_unread_count.
      await supabase
        .from("feedback_threads")
        .update({
          status: "awaiting_user" as any,
          last_message_at: new Date().toISOString(),
          user_unread_count: 1,
        } as any)
        .eq("id", threadId);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["feedback-messages", vars.threadId] });
      queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
    },
  });
}

export function useMarkResolved() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (threadId: string) => {
      // Add system message
      await supabase.from("feedback_messages").insert({
        thread_id: threadId,
        sender_type: "user",
        message: "El usuario marcó este feedback como resuelto.",
        is_system_message: true,
      });

      await supabase
        .from("feedback_threads")
        .update({
          status: "awaiting_finalization" as any,
          resolved_by_user_at: new Date().toISOString(),
          last_message_at: new Date().toISOString(),
          admin_unread_count: 1,
        } as any)
        .eq("id", threadId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
      queryClient.invalidateQueries({ queryKey: ["feedback-messages"] });
    },
  });
}

export function useClearUserUnread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (threadId: string) => {
      await supabase
        .from("feedback_threads")
        .update({ user_unread_count: 0 } as any)
        .eq("id", threadId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
    },
  });
}

// ── Admin hooks ──

export function useAdminThreads() {
  const { user, testerMode } = useAuth();
  const queryClient = useQueryClient();
  const isActiveAdmin = !!user?.isAdmin && !testerMode;

  const query = useQuery({
    queryKey: ["admin-feedback-threads"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feedback_threads")
        .select("*, app_users!feedback_threads_user_id_fkey(email)")
        .order("last_message_at", { ascending: false });
      if (error) throw error;
      return (data ?? []).map((t: any) => ({
        ...t,
        user_email: t.app_users?.email ?? null,
        app_users: undefined,
      })) as FeedbackThread[];
    },
    enabled: isActiveAdmin,
  });

  // Realtime — all threads
  useEffect(() => {
    if (!isActiveAdmin) return;
    const channel = supabase
      .channel("admin_feedback_threads_rt")
      .on("postgres_changes", { event: "*", schema: "public", table: "feedback_threads" }, () => {
        queryClient.invalidateQueries({ queryKey: ["admin-feedback-threads"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [isActiveAdmin, queryClient]);

  const pendingCount = (query.data ?? []).filter(t => t.admin_unread_count > 0).length;

  return { ...query, pendingCount };
}

export function useAdminReply() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadId, message }: { threadId: string; message: string }) => {
      if (!user?.id) throw new Error("Not authenticated");

      await supabase.from("feedback_messages").insert({
        thread_id: threadId,
        sender_type: "admin",
        sender_name: "Chef Natasha",
        message: message.trim(),
      });

      await supabase
        .from("feedback_threads")
        .update({
          status: "awaiting_user" as any,
          last_message_at: new Date().toISOString(),
          last_admin_reply_at: new Date().toISOString(),
          admin_unread_count: 0,
          user_unread_count: 1,
        } as any)
        .eq("id", threadId);

      // Send push + email notification to user
      const { data: threadRow } = await supabase
        .from("feedback_threads")
        .select("user_id, user_name_snapshot")
        .eq("id", threadId)
        .single();

      let userEmail: string | null = null;
      let userMarket: string | null = null;
      let userLang: "es" | "en" | "de" = "es";
      if (threadRow?.user_id) {
        const { data: appUser } = await supabase
          .from("app_users")
          .select("email, market")
          .eq("id", threadRow.user_id)
          .single();
        userEmail = appUser?.email ?? null;
        userMarket = (appUser as any)?.market ?? null;

        // Determine language by market or by EN product ownership
        userLang = userMarket === "us" ? "en" : userMarket === "de" ? "de" : "es";
        if (userLang === "es") {
          const { data: prods } = await supabase
            .from("user_products")
            .select("product_id")
            .eq("user_id", threadRow.user_id);
          const enIds = new Set(["7456362", "8061842", "7381203", "7381661"]);
          if (prods?.some((p: any) => enIds.has(p.product_id))) userLang = "en";
        }
      }
      const thread = threadRow
        ? { ...threadRow, user_email: userEmail, user_lang: userLang }
        : null;

      if (thread?.user_id) {
        supabase.functions
          .invoke("send-push", {
            body: {
              user_id: thread.user_id,
              title: thread.user_lang === "en" ? "Chef Natasha replied to you"
                   : thread.user_lang === "de" ? "Chef Natasha hat geantwortet"
                   : "Chef Natasha respondió tu mensaje",
              body: message.trim().slice(0, 100),
              url: `/soporte/${threadId}`,
              tag: `feedback-${threadId}`,
            },
          })
          .then(({ error }) => {
            if (error) {
              console.error("[Feedback] ✗ Push invoke error:", error);
            }
          })
          .catch((err) => console.error("[Feedback] ✗ Push exception:", err));

        // Also send email notification (fallback for users without push enabled)
        if (thread.user_email) {
          supabase.functions
            .invoke("send-support-email", {
              body: {
                to_email: thread.user_email,
                to_name: thread.user_name_snapshot || undefined,
                thread_id: threadId,
                type: "admin_reply",
                message_preview: message.trim(),
                language: thread.user_lang || "es",
              },
            })
            .then(({ error }) => {
              if (error) console.warn("[Feedback] ✗ Email notification error:", error);
            })
            .catch((err) => console.warn("[Feedback] ✗ Email exception:", err));
        }
      } else {
        console.warn("[Feedback] ⚠ No user_id found for thread:", threadId);
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["feedback-messages", vars.threadId] });
      queryClient.invalidateQueries({ queryKey: ["admin-feedback-threads"] });
      queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
    },
  });
}

export function useAdminFinalize() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (threadId: string) => {
      await supabase.from("feedback_messages").insert({
        thread_id: threadId,
        sender_type: "admin",
        message: "Este feedback ha sido finalizado por el equipo.",
        is_system_message: true,
      });

      await supabase
        .from("feedback_threads")
        .update({
          status: "finalized" as any,
          finalized_by_admin_at: new Date().toISOString(),
          closed_at: new Date().toISOString(),
          closed_by: "admin",
          last_message_at: new Date().toISOString(),
          admin_unread_count: 0,
        } as any)
        .eq("id", threadId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-feedback-threads"] });
      queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
      queryClient.invalidateQueries({ queryKey: ["feedback-messages"] });
    },
  });
}

export function useAdminReopen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (threadId: string) => {
      // Delete the finalization system message
      await supabase
        .from("feedback_messages")
        .delete()
        .eq("thread_id", threadId)
        .eq("is_system_message", true)
        .eq("message", "Este feedback ha sido finalizado por el equipo.");

      await supabase
        .from("feedback_threads")
        .update({
          status: "in_review" as any,
          finalized_by_admin_at: null,
          closed_at: null,
          closed_by: null,
          last_message_at: new Date().toISOString(),
        } as any)
        .eq("id", threadId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-feedback-threads"] });
      queryClient.invalidateQueries({ queryKey: ["feedback-threads"] });
      queryClient.invalidateQueries({ queryKey: ["feedback-messages"] });
    },
  });
}

export function useClearAdminUnread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (threadId: string) => {
      await supabase
        .from("feedback_threads")
        .update({ admin_unread_count: 0 } as any)
        .eq("id", threadId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-feedback-threads"] });
    },
  });
}

export function useAdminDeleteMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ messageId, threadId }: { messageId: string; threadId: string }) => {
      const { error } = await supabase
        .from("feedback_messages")
        .delete()
        .eq("id", messageId);
      if (error) throw error;
      return threadId;
    },
    onSuccess: (threadId) => {
      queryClient.invalidateQueries({ queryKey: ["feedback-messages", threadId] });
      queryClient.invalidateQueries({ queryKey: ["admin-feedback-threads"] });
    },
  });
}

export function useAdminDeleteThread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (threadId: string) => {
      // Cascade deletes messages and attachments via FK ON DELETE CASCADE
      const { error } = await supabase
        .from("feedback_threads")
        .delete()
        .eq("id", threadId);
      if (error) throw error;
      return threadId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-feedback-threads"] });
      queryClient.invalidateQueries({ queryKey: ["user-feedback-threads"] });
    },
  });
}

export function useGenerateAiSuggestion() {
  return useMutation({
    mutationFn: async (threadId: string): Promise<string> => {
      const { data, error } = await supabase.functions.invoke("ai-support-suggest", {
        body: { thread_id: threadId },
      });
      if (error) throw new Error(error.message || "Erro ao chamar a função de IA");
      if (data?.error) throw new Error(data.error);
      return (data?.suggestion as string) ?? "";
    },
  });
}
